import { defineMutation, useMutation, useQueryCache } from '@pinia/colada';

import { useAlertsStore } from '@tallo/design-system';

import { ConnectCodeboxDto } from '../dtos/codebox.dto.interface';
import { getCodeboxIntegrationQueryKey, getCodeboxesQueryKey } from '../queries/codebox.query';
import codeboxService from '../services/codebox.service';

/** The backend rejects bad credentials, so the message it returns is the only thing that tells the user why. */
function serverMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;

  return typeof message === 'string' ? message : fallback;
}

export const useConnectCodebox = defineMutation(() => {
  return useMutation({
    async mutation(payload: ConnectCodeboxDto) {
      await codeboxService.connect(payload);

      // Credentials alone leave an empty picker — the boxes only exist locally after a sync. A sync
      // failure is reported on its own, since the account is connected either way.
      try {
        await codeboxService.sync();
        return { syncError: null };
      } catch (error) {
        return { syncError: serverMessage(error, 'Connected, but the lockboxes could not be loaded.') };
      }
    },

    async onSuccess(result) {
      const queryCache = useQueryCache();

      await queryCache.invalidateQueries({ key: getCodeboxIntegrationQueryKey() });
      await queryCache.invalidateQueries({ key: getCodeboxesQueryKey() });

      const alertsStore = useAlertsStore();

      if (result.syncError) {
        alertsStore.warning(result.syncError);
      } else {
        alertsStore.success('CodeBox account connected');
      }
    },

    onError(error) {
      useAlertsStore().error(serverMessage(error, 'Could not connect to CodeBox. Check the username and password.'));
    },
  });
});

export const useSyncCodeboxes = defineMutation(() => {
  return useMutation({
    mutation() {
      return codeboxService.sync();
    },

    async onSuccess(response) {
      const queryCache = useQueryCache();
      await queryCache.invalidateQueries({ key: getCodeboxesQueryKey() });

      const { total, failed } = response.data;
      const alertsStore = useAlertsStore();

      if (failed) {
        alertsStore.warning(`Synced ${total - failed} of ${total} lockboxes — ${failed} could not be saved.`);
      } else {
        alertsStore.success(`Synced ${total} lockbox(es) from CodeBox`);
      }
    },

    onError(error) {
      useAlertsStore().error(serverMessage(error, 'Could not sync lockboxes from CodeBox'));
    },
  });
});

export const useAssignCodebox = defineMutation(() => {
  return useMutation({
    mutation(payload: { codeboxId: string; propertyId: string }) {
      return codeboxService.assign(payload.codeboxId, payload.propertyId);
    },

    async onSuccess() {
      await useQueryCache().invalidateQueries({ key: getCodeboxesQueryKey() });
    },

    onError(error) {
      const status = (error as { response?: { status?: number } })?.response?.status;

      // The server distinguishes the conflicts (box taken vs property already has one), so prefer its wording.
      useAlertsStore().error(
        serverMessage(
          error,
          status === 409
            ? 'That lockbox is already assigned to another property. Unassign it there first.'
            : 'Could not assign the lockbox',
        ),
      );
    },
  });
});

export const useUnassignCodebox = defineMutation(() => {
  return useMutation({
    mutation(payload: { codeboxId: string }) {
      return codeboxService.unassign(payload.codeboxId);
    },

    async onSuccess() {
      await useQueryCache().invalidateQueries({ key: getCodeboxesQueryKey() });
    },

    onError() {
      useAlertsStore().error('Could not unassign the lockbox');
    },
  });
});
